import { Component, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  EquipmentDetailDto,
  EquipmentMergeDto,
  EquipmentService,
} from '@artemis-software/wr-api';
import { FormControl, Validators } from '@angular/forms';
import { createForm } from '@sonofabit/ng-core';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { PendingChanges } from '@guards/pending-changes.guard';
import { tryWithLoading } from '@/utils/async-utils';
import { currentTenantId } from '@/utils/admin-utils';
import {
  Attachment,
  attachmentDetailToAttachmentReferenceDto,
  attachmentFromDetail,
  attachmentsFromDetails,
  attachmentToMerge,
} from '@shared/form-controls/attachment-upload/attachment-utils';

@Component({
  standalone: false,
  selector: 'wr-tga-type-form',
  templateUrl: './tga-type-form.component.html',
  styleUrls: ['./tga-type-form.component.scss'],
})
export class TgaTypeFormComponent implements PendingChanges {

  readonly saving$ = new BehaviorSubject(false);
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);
  readonly isNew = signal(false);

  private readonly _currentTenantId = currentTenantId();

  tgaTypeId: string | null = null;
  form = this.buildForm(null);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly equipmentService: EquipmentService,
    private readonly dialogService: NbDialogService,
    private readonly toastrService: NbToastrService,
  ) {
    this.route.paramMap.subscribe(params => {
      const id = this.tgaTypeId = params.get('id');
      if (id === 'new') {
        this.isNew.set(true);
        this.isEditing.set(true);
        this.canEdit.set(true);
        this.form = this.buildForm(null);
      } else if (id) {
        this.equipmentService.findById(id).subscribe(dto => {
          this.canEdit.set(dto.permissions.canEdit);
          this.form = this.buildForm(dto);
          if (!this.isEditing()) {
            this.form.disable();
          }
        });
      }
    });
  }

  buildForm(detail: EquipmentDetailDto | null) {
    return createForm(({ field }) => ({
      id: field({ value: detail?.id }),
      name: field({ value: detail?.name, validators: [Validators.required] }),
      facilityElements: field({ value: Array.from(detail?.facilityElements ?? []) }),
      description: field({ value: detail?.description }),
      tenantId: field({ value: detail?.tenant?.id ?? this._currentTenantId(), validators: [Validators.required] }),
      color: field({ value: detail?.color, validators: [Validators.required] }),
      iconReference: field({ value: attachmentFromDetail(detail?.icon) }),
      attachments: field({ value: attachmentsFromDetails(detail?.attachments ?? []) }),
      deprecated: new FormControl(detail?.deprecated ?? false),
    }));
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.form.enable();
    this.form.controls['tenantId'].disable();
  }

  async cancel(): Promise<void> {
    if (this.isNew()) {
      await this.router.navigate(['equipments']);
      return;
    }
    this.isEditing.set(false);
    const id = this.tgaTypeId;
    if (id) {
      const dto = await firstValueFrom(this.equipmentService.findById(id));
      this.form = this.buildForm(dto);
      this.form.disable();
    }
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    if (!this.form.valid) {
      this.toastrService.danger('TGA Typ kann nicht gespeichert werden, es gibt noch Fehler.', 'TGA Typ');
      return;
    }
    await tryWithLoading(this.saving$, async () => {
      const mergeDto = {
        ...this.form.value,
        iconReference: attachmentDetailToAttachmentReferenceDto(this.form.value.iconReference),
        attachments: (this.form.value.attachments ?? []).map((a: Attachment) => attachmentToMerge(a)),
        deprecated: this.form.value.deprecated ?? undefined,
      } as EquipmentMergeDto;
      const saved = await firstValueFrom(this.equipmentService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('TGA Typ wurde erfolgreich gespeichert', 'TGA Typ');
      if (this.isNew()) {
        await this.router.navigate(['equipment', saved.id], { replaceUrl: true });
      } else {
        const dto = await firstValueFrom(this.equipmentService.findById(saved.id));
        this.form = this.buildForm(dto);
        this.form.disable();
      }
    });
  }

  onDelete(): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'TGA Typ löschen',
        message: 'Bist du sicher, dass du den TGA Typ löschen willst?',
      },
    }).onClose.subscribe(async (confirmed) => {
      if (confirmed && this.tgaTypeId) {
        await firstValueFrom(this.equipmentService.deleteById(this.tgaTypeId));
        this.toastrService.success('TGA Typ wurde erfolgreich gelöscht', 'TGA Typ');
        await this.router.navigate(['equipments']);
      }
    });
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }
}
